/* eslint-disable react/require-default-props */
import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FileSectionRow, Section } from '../../interfaces';
import CustomerTransactionsSection from './CustomerTransactionsSection';
import PaymentSection from './PaymentSection';
import SectionHeader from './SectionHeader';
import SectionPhoneRow from './SectionPhoneRow';
import SectionTextRow from './SectionTextRow';
import TransactionsSection from './TransactionsSection';
import CheckoutTransactionsSection from './CheckoutTransactionsSection';
import AppointmentTransfersSection from './AppointmentTransfersSection';
import CheckoutTransfersSection from './CheckoutTransfersSection';
import ActivityHistorySection from './ActivityHistorySection';
import TransferDetailsSummary from './TransferDetailsSummary';
import ClientCardBody from './ClientCardBody';
import ServiceDetailBody from './ServiceDetailBody';
import SectionComponentRow from './SectionComponentRow';
import { cx } from '../../helpers/global';
import SectionFileRow from './SectionFileRow';
import SectionColorRow from './SectionColorRow';
import SectionLinkRow from './SectionLinkRow';
import { SECTION_LABEL_SX } from './sidebarInfoGrid';

type Props = Section & {
  modalName: string;
  flat?: boolean;
};

export default function SideBarSection({
  name,
  title,
  rows,
  modalName,
  values,
  buttonText,
  buttonAction,
  noGap,
  defaultExpanded,
  hideHeader,
  summary,
  microLabel,
  flat,
}: Props) {
  const body = (
    <>
      {name === 'Payment' && (
        <PaymentSection
          action={values.checkoutAction}
          refreshModal={values.refreshModal}
          appointment={values.appointment}
          useSpecStyle={values.useSpecStyle}
          key={`${modalName}-payment`}
        />
      )}
      {name === 'Appointment Transactions' && (
        <TransactionsSection
          appointment={values.appointment}
          useSpecStyle={values.useSpecStyle}
        />
      )}
      {name === 'Transfer Details' && (
        <AppointmentTransfersSection
          payment={values?.payment}
          appointment={values.appointment}
          useSpecStyle={flat}
        />
      )}
      {name === 'Checkout Transactions' && (
        <CheckoutTransactionsSection paymentsGroup={values.paymentsGroup} />
      )}
      {name === 'Checkout Transfer Details' && (
        <CheckoutTransfersSection paymentsGroup={values.paymentsGroup} />
      )}
      {name === 'Customer Transactions' && (
        <CustomerTransactionsSection customer={values.customer} />
      )}
      {name === 'Activity History' && (
        <ActivityHistorySection
          appointment={values.appointment}
          loadedUser={values.loadedUser}
        />
      )}
      {name === 'Client Card' && (
        <ClientCardBody
          appointment={values.appointment}
          onOpenChat={values.onOpenChat}
          showChat={values.showChat}
          navigateToCustomer={values.navigateToCustomer}
        />
      )}
      {name === 'Service Detail' && (
        <ServiceDetailBody
          gridFields={values.gridFields}
          noteFields={values.noteFields}
          photoRows={values.photoRows}
        />
      )}
      {name !== 'Payment' &&
        name !== 'Appointment Transactions' &&
        name !== 'Transfer Details' &&
        name !== 'Activity History' &&
        name !== 'Client Card' &&
        name !== 'Service Detail' &&
        rows.map(sectionRow => {
          if (!sectionRow.value?.[0]) return null;
          return (
            <React.Fragment
              key={
                sectionRow.id ||
                (
                  sectionRow.label +
                  sectionRow.type +
                  sectionRow.value[0]
                ).toString()
              }>
              {sectionRow.type === 'IMAGE' && (
                <SectionFileRow
                  label={sectionRow.label}
                  values={(sectionRow.value as string[])?.map(url => ({
                    id: url,
                    url,
                    name: 'url',
                    type: 'image',
                  }))}
                  key={`${modalName}-${sectionRow.label}`}
                />
              )}
              {sectionRow.type === 'TEXT' && (
                <SectionTextRow
                  label={sectionRow.label}
                  values={sectionRow.value as string[]}
                  key={`${modalName}-${sectionRow.label}`}
                />
              )}
              {sectionRow.type === 'SUBTEXT' && (
                <SectionTextRow
                  label={sectionRow.label}
                  values={sectionRow.value as string[]}
                  key={`${modalName}-${sectionRow.label}`}
                  isSubtext
                />
              )}
              {sectionRow.type === 'LINK' && (
                <SectionLinkRow
                  label={sectionRow.label}
                  values={sectionRow.value}
                  key={`${modalName}-${sectionRow.label}`}
                />
              )}
              {sectionRow.type === 'PHONE' && (
                <SectionPhoneRow
                  label={sectionRow.label}
                  values={sectionRow.value as string[]}
                  key={`${modalName}-${sectionRow.label}`}
                />
              )}
              {sectionRow.type === 'NODE' && (
                <SectionComponentRow
                  label={sectionRow.label}
                  values={sectionRow.value?.[0] as React.ReactNode}
                  key={`${modalName}-${sectionRow.label}`}
                />
              )}
              {sectionRow.type === 'FILE' && (
                <SectionFileRow
                  label={sectionRow.label}
                  values={(sectionRow as FileSectionRow).value}
                  key={`${modalName}-${sectionRow.label}`}
                />
              )}
              {sectionRow.type === 'COLOR' && (
                <SectionColorRow
                  label={sectionRow.label}
                  values={sectionRow.value as string[]}
                />
              )}
            </React.Fragment>
          );
        })}
    </>
  );

  if (defaultExpanded !== undefined) {
    return (
      <Accordion
        defaultExpanded={defaultExpanded}
        disableGutters
        elevation={0}
        className={cx('sidebar-section-accordion', flat && 'flat')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}>
            <Typography
              variant={flat ? undefined : 'h3'}
              component="h3"
              sx={
                microLabel
                  ? SECTION_LABEL_SX
                  : flat
                  ? {
                      fontSize: 'var(--sidebar-font-base)',
                      fontWeight: 700,
                      color: 'var(--sidebar-ink)',
                    }
                  : undefined
              }>
              {title ?? name}
            </Typography>
            {name === 'Transfer Details' ? (
              <TransferDetailsSummary
                appointment={values.appointment}
                payment={values?.payment}
                useSpecStyle={flat}
              />
            ) : (
              summary && (
                <Typography
                  variant={flat ? undefined : 'body2'}
                  sx={
                    flat
                      ? {
                          fontSize: 'var(--sidebar-font-sm)',
                          color: 'var(--sidebar-tertiary)',
                        }
                      : undefined
                  }
                  color={flat ? undefined : 'text.secondary'}>
                  {summary}
                </Typography>
              )
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails
          className={cx(
            'sidebar-section-accordion-details',
            noGap && 'no-gap',
          )}>
          {body}
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <div className={cx('sidebar-section', noGap && 'no-gap', flat && 'flat')}>
      {(!hideHeader || (buttonText && buttonAction)) && (
        <SectionHeader
          name={title ?? name}
          buttonText={buttonText}
          buttonAction={buttonAction}
          microLabel={microLabel}
          hideTitle={hideHeader}
          flat={flat}
        />
      )}
      {body}
    </div>
  );
}
